import type { AbstractAnalyticsDispatcher } from "@asurion-core/eventualize";
import type { SessionSummary } from "@expert/sales-tools";

import {
    addSessionSummaryMessage,
    deactivatedSummaryMessage,
    deactivatePreviousSessionSummaryMessage,
    useTimelineStore,
} from "@asurion-core/expert-workspace-timeline";
import { trackFullStoryEvent } from "@expert/monitoring";
import { useCallback } from "react";

const NO_SUMMARY_AVAILABLE_MESSAGE = "No summary available from past 30 days.";

export interface UsePreviousSessionSummaryParams {
    sessionId: string;
    isPreviousSessionSummaryEnabled: boolean;
    isChat: boolean;
    rootDispatcher: AbstractAnalyticsDispatcher;
}

export interface UsePreviousSessionSummaryReturn {
    /**
     * Adds a previous session summary to the timeline.
     * If no summary is provided, shows "No summary available" message.
     * Automatically deactivates any existing summary first.
     */
    addToTimeline: (prevSummary?: SessionSummary) => void;
}

export function usePreviousSessionSummary(params: UsePreviousSessionSummaryParams): UsePreviousSessionSummaryReturn {
    const { sessionId, isPreviousSessionSummaryEnabled, isChat, rootDispatcher } = params;

    const timelineState = useTimelineStore().getTimelineState(sessionId);

    const addToTimeline = useCallback(
        (prevSummary?: SessionSummary) => {
            if (!isPreviousSessionSummaryEnabled || isChat) return;

            const lastPrevSummaryMessage = timelineState.messages.findLast((msg) => msg.type === "SessionSummary");

            const lastSummary = lastPrevSummaryMessage?.metadata?.sessionSummary;
            const shouldHideLastPrevSummaryMessage =
                lastSummary && lastPrevSummaryMessage?.text !== deactivatedSummaryMessage;

            if (shouldHideLastPrevSummaryMessage) {
                void rootDispatcher.dispatchBusinessEvent("PreviousSessionSummaryHidden", {
                    hiddenReason: "mdnUpdate",
                    newSummaryExists: !!prevSummary,
                });
                deactivatePreviousSessionSummaryMessage(sessionId, lastSummary.timestamp);
            }

            const header = "Previous session";
            const sessionType = "previous";

            if (!prevSummary || !prevSummary.summary) {
                addSessionSummaryMessage(sessionId, {
                    header,
                    summary: NO_SUMMARY_AVAILABLE_MESSAGE,
                    sessionType,
                    timestamp: new Date().toString(),
                });
                return;
            }

            const { summary, timestamp, resolutionStatus, callSid } = prevSummary;

            void rootDispatcher.dispatchBusinessEvent("PreviousSessionSummaryShown", {
                previousSummaryTimestamp: timestamp,
            });

            trackFullStoryEvent("PreviousSessionSummaryShown", { previousSummaryTimestamp: timestamp });

            addSessionSummaryMessage(sessionId, {
                header,
                summary,
                timestamp,
                resolutionStatus,
                sessionType,
                previousSessionId: callSid,
            });
        },
        [sessionId, isPreviousSessionSummaryEnabled, isChat, rootDispatcher, timelineState.messages],
    );

    return {
        addToTimeline,
    };
}
